export type { ContextFileSelection } from './contextDocuments';
export type { Permission, Role, RoleRef } from './Role';

import type { RoleRef } from './Role';

export type UserRole = 'lawyer' | 'hr' | 'ops' | 'admin';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarInitials: string;
  projectId: string;
  roleId: string;
  isDemo: boolean;
}

export interface ScenarioStep {
  label: string;
}

export interface ScenarioModel {
  id: string;
  name: string;
}

export interface Scenario {
  id: string;
  number: string;
  title: string;
  shortDescription: string;
  steps: ScenarioStep[];
  result: string;
  instruction: string;
  systemPrompt: string | null;
  model: ScenarioModel | null;
  minFiles: number;
  maxFiles: number;
  roles: string[];
  roleTitle?: string;
}

export interface MockFile {
  id: string;
  name: string;
  ext: string;
  size: string;
}

export interface ChatMessage {
  id: string;
  role: 'ai' | 'user';
  content: string;
  timestamp: string;
}

export interface HistorySession {
  id: string;
  scenarioId: string;
  title: string;
  date: string;
  filesCount: number;
  messages: ChatMessage[];
}

export interface KpiData {
  totalRequests: number;
  requestsDelta: number;
  tokensUsed: number;
  tokensLimit: number;
  documentsProcessed: number;
  avgResponseTime: number;
  activeDays: number;
}

export interface DayActivity {
  day: string;
  date?: string; // YYYY-MM-DD, присутствует в реальных данных
  count: number;
}

export interface ScenarioStat {
  scenarioId: string;
  title: string;
  requests: number;
  tokens: number;
}

export interface FeedItem {
  id: string;
  scenarioId: string;
  scenarioTitle: string;
  fileName: string;
  time: string;
  color: string;
}

/**
 * Пользователь в админ-разделе — соответствует модели `User` бэкенда
 * (`/user/all`, `/user/{id}`). Все поля — реальные данные API.
 */
export interface AdminUser {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  isAdmin: boolean;
  createdAt: number | null;
  roles: RoleRef[];
  hasYandexAccess: boolean;
  hasConfluenceAccess: boolean;
  hasJiraAccess: boolean;
  /** Инициалы для аватара (вычисляются на клиенте). */
  avatarInitials: string;
}

export interface UserStats {
  userId: string;
  totalRequests: number;
  requestsDelta: number;
  tokensUsed: number;
  tokenLimit: number;
  documentsProcessed: number;
  avgResponseTime: number;
  activeDays: number;
  weekActivity: DayActivity[];
  scenarioStats: ScenarioStat[];
  feed: FeedItem[];
  monthTokens: { month: string; used: number }[];
}

// ─── Chat listing (backend /chats) ───────────────────────────────────────────
export interface ChatListing {
  id: string;
  title: string | null;
  scenario_id: string | null;
  project_id: string | null;
  user_id: string;
  last_message_at: number | null;
  created_at: number;
  updated_at: number;
}

// ─── Chat (совместимо с useStreamingMessage) ────────────────────────────────
export interface ChatMsg {
  role: 'AI' | 'User';
  content: string;
  created_at: string;
  modelName?: string;
}

// ─── Context files (backend: projects/context-documents) ────────────────────
export const ContextDocumentTargetType = {
  Chat: 'chat',
  Project: 'project',
  Scenario: 'scenario',
} as const;

export type ContextDocumentTargetType =
  (typeof ContextDocumentTargetType)[keyof typeof ContextDocumentTargetType];

export interface ContextFile {
  id: string;
  name: string;
  created_at: string;
  is_active?: boolean;
  file: {
    mime_type: string;
    size: number;
  } | null;
}

// ─── IndexDB Session ─────────────────────────────────────────────────────────
export interface SessionFile {
  id: string;
  name: string;
  usedInRun: boolean;
}

export interface SessionMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
  isSystemRun: boolean;
  tokenEstimate: number;
  modelName?: string;
}

export interface SessionStats {
  messageCount: number;
  aiResponseCount: number;
  totalCharsUser: number;
  totalCharsAi: number;
  filesCount: number;
  avgAiResponseLength: number;
}

export interface ScenarioSession {
  id: string;
  scenarioId: string;
  scenarioTitle: string;
  scenarioRole: UserRole;
  projectId: string; // string: поддерживает числа и UUID
  userId: string;
  userName: string;
  startedAt: string;
  endedAt?: string;
  status: 'active' | 'completed';
  files: SessionFile[];
  messages: SessionMessage[];
  stats: SessionStats;
}
